import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { number, object, string } from 'yup';

import { useCheck } from 'Src/feature/check/useCheck';
import { useCurrentCheck } from 'Src/feature/check/useCurrentCheck';
import { getTypedFormComponents } from 'Src/feature/form/getTypedFormComponents';
import { useForm } from 'Src/feature/form/useForm';
import { useOrderType } from 'Src/feature/order-type/useOrderType';
import { useTerminal } from 'Src/feature/terminal/useTerminal';

interface FormModel {
  partySize?: number;
  labelPrefix: string;
  orderTypeId?: number;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const sectionId = 'useCheckInformationTab';

const { Form, Field } = getTypedFormComponents<FormModel>();

const useCheckInformationTab = () => {
  const { t } = useTranslation();

  const currentCheck = useCurrentCheck();
  const updateCheckInfo = useCheck.mutate.updateInfo();
  const orderTypes = useOrderType.query.list();
  const terminal = useTerminal.query.get();

  // labelPrefix is not currently returned from the server, so we need to extract it from the label,
  // which is a concatenation of the label prefix and check number
  const labelPrefix = useMemo(() => currentCheck.query.data?.label.replace(` ${currentCheck.query.data.number}`, ''), [currentCheck.query.data?.number, currentCheck.query.data?.label]);
  const isLabelRequired = terminal.data?.terminal.terminalSettings.checkNameInputType === 'Required';
  const isDefaultViewNewCheck = terminal.data?.terminal.terminalSettings.defaultView === 'NewCheck';
  const hasCustomLabel = useMemo(() => !labelPrefix?.match('Guest'), [labelPrefix]);

  const initialPartySize = currentCheck.query.data?.partySize;
  // Max party size is the set to be the maximum of 99 or the current party size
  // This ensures that if the party size was set higher than 99, it remains valid
  // and the user can decrease it, but not increase it further to prevent overflow issues
  const maxPartySize = Math.max(99, initialPartySize ?? 0);

  const form = useForm<FormModel>({
    defaultValues: {
      partySize: initialPartySize,
      orderTypeId: currentCheck.query.data?.orderTypeId,
      labelPrefix: isLabelRequired && isDefaultViewNewCheck && !hasCustomLabel ? '' : labelPrefix,
    },
    schema: object().shape({
      labelPrefix: string().label(t('check.labels.labelPrefix')).max(99).required(),
      partySize: number().label(t('labels.partySize')).transform((value: unknown) => Number.isNaN(value) ? null : value).nullable().min(0).max(maxPartySize).required(),
    }),
  });

  const orderTypeOptions = (orderTypes.data ?? []).map((o) => ({ value: o.id, label: o.name }));

  const onUpdateCheckInfo = async () => {
    await form.action.submitIfValid(async ({ values }) =>
      updateCheckInfo.mutate({
        checkId: currentCheck.query.data?.id as number,
        info: {
          ...values,
          partySize: parseInt((values.partySize ?? 0).toString()),
        }
      })
    );
  };

  useEffect(() => {
    form.action.reset({
      partySize: initialPartySize,
      orderTypeId: currentCheck.query.data?.orderTypeId,
      labelPrefix: isLabelRequired && isDefaultViewNewCheck && !hasCustomLabel ? '' : labelPrefix,
    });
    // kick off form validation on reset
    form.action.setValue('labelPrefix', isLabelRequired && isDefaultViewNewCheck && !hasCustomLabel ? '' : (labelPrefix ?? ''), { shouldDirty: true, shouldTouch: true, shouldValidate: true });
  }, [
    labelPrefix,
    initialPartySize,
    currentCheck.query.data?.orderTypeId,
    form.action,
    isLabelRequired,
    isDefaultViewNewCheck,
    hasCustomLabel,
  ]);

  return {
    form,
    check: currentCheck.query.data,
    options: {
      orderType: orderTypeOptions,
    },
    onUpdateCheckInfo: () => {
      void onUpdateCheckInfo();
    },
  };
};

export { Field, Form, useCheckInformationTab };
